import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Heart, Search, ShoppingBag, Star, Trash2 } from "lucide-react";
import ProductGrid from "../components/ProductGrid";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import useScrollReveal from "../hooks/useScrollReveal";

export default function FavoritesPage() {
  const { user } = useAuth();
  const { products, favorites, toggleFavorite, getDisplayPrice } = useShop();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  useScrollReveal();

  const favoriteProducts = useMemo(() => {
    const fav = products.filter((product) => favorites.includes(product.id));
    const q = query.trim().toLocaleLowerCase("tr-TR");
    const filtered = fav.filter((product) => {
      const text = [product.name, product.type, product.usage, product.series, product.description].join(" ").toLocaleLowerCase("tr-TR");
      return !q || text.includes(q);
    });

    return [...filtered].sort((a, b) => {
      if (sort === "priceAsc") return getDisplayPrice(a) - getDisplayPrice(b);
      if (sort === "priceDesc") return getDisplayPrice(b) - getDisplayPrice(a);
      if (sort === "stock") return Number(b.stock || 0) - Number(a.stock || 0);
      return favorites.indexOf(b.id) - favorites.indexOf(a.id);
    });
  }, [products, favorites, query, sort, getDisplayPrice]);

  const stats = useMemo(() => ({
    count: favoriteProducts.length,
    inStock: favoriteProducts.filter((product) => Number(product.stock || 0) > 0).length,
    categories: new Set(favoriteProducts.map((product) => product.type).filter(Boolean)).size,
  }), [favoriteProducts]);

  if (!user) {
    return <section className="px-5 py-16 text-center md:px-10"><h1 className="mb-4 text-3xl font-bold">Favorilerini görmek için giriş yap</h1><Link to="/login" className="rounded-xl bg-stone-900 px-6 py-3 text-white">Giriş Yap</Link></section>;
  }

  return (
    <section className="px-4 py-8 sm:px-6 md:px-10">
      <div className="mb-7 overflow-hidden rounded-[2rem] bg-gradient-to-br from-stone-950 via-stone-900 to-emerald-950 p-6 text-white shadow-soft sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-emerald-200">Sevinç Outdoor</p>
            <h1 className="text-3xl font-black sm:text-4xl">Favorilerim</h1>
            <p className="mt-2 max-w-2xl text-sm text-stone-300">Beğendiğin ürünleri tek ekranda takip et, stok durumunu kontrol et ve alışverişe hızlıca devam et.</p>
          </div>
          <Link to="/products" className="w-fit rounded-2xl bg-white px-5 py-3 text-sm font-bold text-stone-950">Ürünleri İncele</Link>
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Favori Ürün</p><strong className="mt-1 block text-2xl">{stats.count}</strong></div>
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Stokta Olan</p><strong className="mt-1 block text-2xl">{stats.inStock}</strong></div>
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Kategori</p><strong className="mt-1 block text-2xl">{stats.categories}</strong></div>
      </div>

      <div className="mb-6 flex flex-col gap-3 rounded-3xl bg-white p-4 shadow-soft lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Favorilerde ürün, kategori veya kullanım alanı ara" className="w-full rounded-2xl border border-stone-200 py-3 pl-11 pr-4 outline-none focus:border-emerald-600" />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-2xl border border-stone-200 bg-white px-4 py-3 outline-none focus:border-emerald-600">
          <option value="newest">Son eklenen</option>
          <option value="priceAsc">Fiyat artan</option>
          <option value="priceDesc">Fiyat azalan</option>
          <option value="stock">Stok durumuna göre</option>
        </select>
      </div>

      {!favoriteProducts.length ? (
        <div className="rounded-3xl bg-white p-10 text-center shadow-soft">
          <Heart className="mx-auto mb-4 text-stone-400" size={46} />
          <h2 className="text-xl font-bold">Favori ürün bulunamadı</h2>
          <p className="mt-2 text-stone-500">Ürün kartlarındaki kalp ikonuna tıklayarak favorilerine ekleyebilirsin.</p>
          <Link to="/products" className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-stone-950 px-5 py-3 text-sm font-bold text-white"><ShoppingBag size={18} />Alışverişe Başla</Link>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="hidden overflow-hidden rounded-[1.8rem] bg-white shadow-soft ring-1 ring-stone-100 lg:block">
            <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.5fr] gap-4 border-b border-stone-100 px-5 py-4 text-xs font-bold uppercase tracking-widest text-stone-400">
              <span>Ürün</span><span>Kategori</span><span>Stok</span><span>İşlem</span>
            </div>
            {favoriteProducts.map((product) => (
              <div key={product.id} className="grid grid-cols-[1.4fr_0.7fr_0.7fr_0.5fr] items-center gap-4 border-b border-stone-100 px-5 py-4 last:border-b-0">
                <Link to={`/products/${product.id}`} className="flex items-center gap-4">
                  <img src={product.image || product.images?.[0] || "/product-knife-catalog.png"} alt={product.name} className="h-20 w-20 rounded-2xl object-cover" />
                  <div className="min-w-0">
                    <h3 className="font-bold text-stone-950">{product.name}</h3>
                    <p className="mt-1 flex items-center gap-1 text-sm text-stone-500"><Star size={15} className="text-amber-500" fill="currentColor" />{product.reviewSummary?.count ? Number(product.reviewSummary.average || 0).toFixed(1) : "Yeni ürün"}</p>
                  </div>
                </Link>
                <span className="font-semibold text-stone-700">{product.type || "-"}</span>
                <span className={Number(product.stock || 0) > 0 ? "font-bold text-emerald-700" : "font-bold text-red-600"}>{Number(product.stock || 0) > 0 ? `${product.stock} adet` : "Stok yok"}</span>
                <button onClick={() => toggleFavorite(product.id)} className="inline-flex w-fit items-center gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600"><Trash2 size={17} />Kaldır</button>
              </div>
            ))}
          </div>

          <div className="lg:hidden">
            <ProductGrid products={favoriteProducts} />
          </div>
        </div>
      )}

      <div className="mt-6 rounded-3xl border border-emerald-100 bg-emerald-50 p-5 text-sm leading-6 text-emerald-900">
        <div className="mb-2 flex items-center gap-2 font-bold"><Box size={18} />Kurumsal takip notu</div>
        Favoriler listesi kullanıcı hesabına bağlıdır; kullanıcı silinmediği sürece favori kayıtları korunur. Ürün silinirse yalnızca ilgili ürün favorilerden düşer.
      </div>
    </section>
  );
}
